// ─── Complete Auto Pagination Navigation Utility ─────────────────────────────

/**
 * Complete solution for automatic pagination navigation with state management.
 *
 * Features:
 * - Automatic pagination state preservation (page, rowsPerPage, filters, search, sort)
 * - URL-based state management for browser back/forward support
 * - Minimal changes to existing components (2-3 lines)
 * - Works with any listing component pattern
 * - Performance optimized with minimal re-renders
 *
 * Usage:
 * 1. Replace existing pagination state with useCompletePaginationNavigation
 * 2. Replace navigate calls with provided navigation functions
 * 3. That's it! Complete pagination navigation works everywhere
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from '@/config/constants';

// ─── Configuration ────────────────────────────────────────────────────────────

const MAX_AGE = 3600000; // 1 hour

// ─── Storage Functions (Optimized) ───────────────────────────────────────────

const getStorageKey = (path: string): string => `pn_state_${path.replace(/\//g, '_')}`;

const saveState = (path: string, search: string): void => {
  if (!search) return;
  try {
    const storageKey = getStorageKey(path);
    sessionStorage.setItem(storageKey, JSON.stringify({
      p: path,
      s: search,
      t: Date.now()
    }));
  } catch {
    // Silently fail - don't break the app
  }
};

const loadState = (path: string): string | null => {
  try {
    const storageKey = getStorageKey(path);
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    
    // Check if valid and not expired
    if (data.p !== path || Date.now() - data.t > MAX_AGE) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    
    return data.s;
  } catch {
    return null;
  }
};

const clearState = (path: string): void => {
  try {
    const storageKey = getStorageKey(path);
    sessionStorage.removeItem(storageKey);
  } catch {
    // Silently fail
  }
};

// ─── Complete Pagination Hook ─────────────────────────────────────────────────

interface CompletePaginationOptions {
  defaultPage?: number;
  defaultRowsPerPage?: number;
  defaultSortDirection?: 'asc' | 'desc';
  defaultSortKey?: string;
}

/**
 * Complete pagination navigation hook that handles ALL pagination state.
 * This replaces manual pagination state management with URL-based state.
 */
export const useCompletePaginationNavigation = (
  navigate: any,
  listingPath: string,
  options: CompletePaginationOptions = {}
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  const {
    defaultPage = 0,
    defaultRowsPerPage = DEFAULT_PAGE_SIZE,
    defaultSortDirection = 'desc',
    defaultSortKey = null
  } = options;

  // ─── URL-based State Management ─────────────────────────────────────────────
  
  const page = useMemo(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? Math.max(0, parseInt(pageParam) - 1) : defaultPage;
  }, [searchParams, defaultPage]);

  const rowsPerPage = useMemo(() => {
    const limitParam = searchParams.get('limit');
    return limitParam ? parseInt(limitParam) : defaultRowsPerPage;
  }, [searchParams, defaultRowsPerPage]);

  const searchTerm = useMemo(() => {
    return searchParams.get('search') || '';
  }, [searchParams]);

  const sortKey = useMemo(() => {
    return searchParams.get('sort') || defaultSortKey;
  }, [searchParams, defaultSortKey]);

  const sortDirection = useMemo(() => {
    const direction = searchParams.get('sortDir');
    return (direction as 'asc' | 'desc') || defaultSortDirection;
  }, [searchParams, defaultSortDirection]);

  const filters = useMemo(() => {
    const filterObj: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (!['page', 'limit', 'search', 'sort', 'sortDir'].includes(key)) {
        filterObj[key] = value;
      }
    });
    return filterObj;
  }, [searchParams]);

  // ─── State Update Functions ─────────────────────────────────────────────────

  const updateParams = useCallback((updates: Record<string, any>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      
      Object.entries(updates).forEach(([key, value]) => {
        // For date filters (gte/lte), allow empty strings to be converted to null
        if ((key.endsWith('__gte') || key.endsWith('__lte')) && value === '') {
          newParams.delete(key); // Remove empty date filters from URL
        } else if (value === null || value === undefined || value === "") {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      
      return newParams;
    });
  }, [setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    updateParams({ page: newPage + 1 });
  }, [updateParams]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    updateParams({
      limit: newRowsPerPage,
      page: 1 // Reset to first page when changing rows per page
    });
  }, [updateParams]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParams({
      search: event.target.value,
      page: 1 // Reset to first page when searching
    });
  }, [updateParams]);

  const handleSortChange = useCallback((newSortKey: string) => {
    const isSameSortKey = sortKey === newSortKey;
    const newDirection = isSameSortKey
      ? (sortDirection === 'asc' ? 'desc' : 'asc')
      : 'asc';
    
    updateParams({
      sort: newSortKey,
      sortDir: newDirection,
      page: 1 // Reset to first page when sorting
    });
  }, [sortKey, sortDirection, updateParams]);

  const handleFilterChange = useCallback((keyOrUpdates: string | Record<string, any>, value?: any) => {
    if (typeof keyOrUpdates === 'string') {
      updateParams({
        [keyOrUpdates]: value,
        page: 1 // Reset to first page when filtering
      });
    } else {
      updateParams({
        ...keyOrUpdates,
        page: 1 // Reset to first page when filtering
      });
    }
  }, [updateParams]);

  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    // Keep only page and limit
    newParams.set('page', '1');
    newParams.set('limit', String(rowsPerPage));
    setSearchParams(newParams);
  }, [rowsPerPage, setSearchParams]);

  // ─── API Parameters ─────────────────────────────────────────────────────────

  const getApiParams = useCallback((options?: { sortFieldMapping?: Record<string, string> }) => {
    const params: Record<string, any> = {
      page: page + 1,
      limit: rowsPerPage,
    };
    
    if (searchTerm) params.search = searchTerm;
    
    // Add all filters
    Object.entries(filters).forEach(([key, value]) => {
      // For date filters (gte/lte), include null values to properly clear filters
      if (key.endsWith('__gte') || key.endsWith('__lte')) {
        if (value !== undefined) {
          params[key] = value === '' ? null : value;
        }
      } else {
        // For other filters, exclude null, undefined, and empty string values
        if (value !== null && value !== undefined && value !== '') {
          params[key] = value;
        }
      }
    });

    if (sortKey) {
      const backendField = options?.sortFieldMapping?.[sortKey] || sortKey;
      params.ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;
    }
    
    return params;
  }, [page, rowsPerPage, searchTerm, filters, sortKey, sortDirection]);

  // ─── Navigation Functions ───────────────────────────────────────────────────

  const toDetail = useCallback((id: string | number) => {
    const currentSearch = location.search;
    if (currentSearch && location.pathname === listingPath) {
      saveState(listingPath, currentSearch);
    }
    navigate(`${listingPath}/${id}`);
  }, [navigate, listingPath, location]);

  const toEdit = useCallback((id: string | number) => {
    const currentSearch = location.search;
    if (currentSearch && location.pathname === listingPath) {
      saveState(listingPath, currentSearch);
    }
    navigate(`${listingPath}/${id}/edit`);
  }, [navigate, listingPath, location]);

  const toCreate = useCallback(() => {
    const currentSearch = location.search;
    if (currentSearch && location.pathname === listingPath) {
      saveState(listingPath, currentSearch);
    }
    navigate(`${listingPath}/create`);
  }, [navigate, listingPath, location]);

  const toNew = useCallback(() => {
    const currentSearch = location.search;
    if (currentSearch && location.pathname === listingPath) {
      saveState(listingPath, currentSearch);
    }
    navigate(`${listingPath}/new`);
  }, [navigate, listingPath, location]);

  const toListing = useCallback(() => {
    const savedSearch = loadState(listingPath);
    const targetUrl = savedSearch ? `${listingPath}${savedSearch}` : listingPath;
    navigate(targetUrl);
    clearState(listingPath);
  }, [navigate, listingPath]);

  const backToListing = useCallback(() => {
    const savedSearch = loadState(listingPath);
    const targetUrl = savedSearch ? `${listingPath}${savedSearch}` : listingPath;
    navigate(targetUrl);
    // Don't clear state here - keep it for potential future navigation
  }, [navigate, listingPath]);

  // ─── Custom Navigation Functions for Special URL Patterns ─────────────────────

  const toEditCustom = useCallback((id: string | number, customPath: string) => {
    const currentSearch = location.search;
    if (currentSearch && location.pathname === listingPath) {
      saveState(listingPath, currentSearch);
    }
    navigate(customPath);
  }, [navigate, listingPath, location]);

  const toDetailCustom = useCallback((id: string | number, customPath: string) => {
    const currentSearch = location.search;
    if (currentSearch && location.pathname === listingPath) {
      saveState(listingPath, currentSearch);
    }
    navigate(customPath);
  }, [navigate, listingPath, location]);

  // ─── Return Complete Interface ───────────────────────────────────────────────

  return {
    // Pagination state
    page,
    rowsPerPage,
    searchTerm,
    sortKey,
    sortDirection,
    filters,
    
    // State handlers
    handlePageChange,
    handleRowsPerPageChange,
    handleSearchChange,
    handleSortChange,
    handleFilterChange,
    clearFilters,
    updateParams,
    
    // API helpers
    getApiParams,
    
    // Navigation functions
    toDetail,
    toEdit,
    toCreate,
    toNew,
    toListing,
    backToListing,
    
    // Custom navigation functions for special URL patterns
    toEditCustom,
    toDetailCustom,
  };
};

// ─── Simple Hook for Navigation Only (Backward Compatibility) ─────────────────

/**
 * Simple hook that provides only navigation functions (for backward compatibility).
 * Use useCompletePaginationNavigation for full functionality.
 */
export const useAutoPaginationNavigation = (navigate: any, listingPath: string) => {
  const location = useLocation();
  
  return {
    toDetail: (id: string | number) => {
      const currentSearch = location.search;
      if (currentSearch && location.pathname === listingPath) {
        saveState(listingPath, currentSearch);
      }
      navigate(`${listingPath}/${id}`);
    },
    toEdit: (id: string | number) => {
      const currentSearch = location.search;
      if (currentSearch && location.pathname === listingPath) {
        saveState(listingPath, currentSearch);
      }
      navigate(`${listingPath}/${id}/edit`);
    },
    toCreate: () => {
      const currentSearch = location.search;
      if (currentSearch && location.pathname === listingPath) {
        saveState(listingPath, currentSearch);
      }
      navigate(`${listingPath}/create`);
    },
    toListing: () => {
      const savedSearch = loadState(listingPath);
      const targetUrl = savedSearch ? `${listingPath}${savedSearch}` : listingPath;
      navigate(targetUrl);
      clearState(listingPath);
    },
    backToListing: () => {
      const savedSearch = loadState(listingPath);
      const targetUrl = savedSearch ? `${listingPath}${savedSearch}` : listingPath;
      navigate(targetUrl);
      // Don't clear state here - keep it for potential future navigation
    },
  };
};


export default useCompletePaginationNavigation;